import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
    Badge,
    Box,
    Button, Checkbox,
    Flex,
    FormControl, FormErrorMessage,
    FormLabel,
    Input, Link,
    Text,
    Textarea, Menu, MenuButton, MenuList, MenuItem, Portal
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useDisclosure, CloseButton } from "@chakra-ui/react";
import { Formik, Form, Field } from 'formik';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import EventsService from '../../../../services/events/eventsService';
import { useEffect, useState, useRef } from "react";
import FileUploadInput from "../../../../shared/components/FileUploadInput/FileUploadInput";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import TicketModal from "../../../../shared/components/Ticket/TicketModal";
import { useTranslation } from "react-i18next";
import { createEventValidationSchema } from "../../../../utils/validation/schemas";
import ErrorInput from "../../../../shared/components/ErrorInput/ErrorInput";
import ErrorEventForm from "../../components/ErrorEventForm/ErrorEventForm";
import { parseISO, format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { normalizeTickets } from '../../../../utils/eventHelpers';
import { phonePrefixes } from '../../../../utils/phonePrefixes';
import ReactCountryFlag from "react-country-flag";
import QuillEditor from "../../../../shared/components/QuillEditor/QuillEditor";
import { useAppToast } from "../../../../hooks/useAppToast";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";
import AddressAutocompleteInput from "../../../../shared/components/AddressAutocompleteInput/AddressAutocompleteInput";
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";

const EditEventForm = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("default");
    const { user, checkIntegrationStatus } = useAuth();
    const isSuperAdmin = user?.roles?.includes('SuperAdmin') ?? false;

    const location = useLocation();
    const toast = useAppToast();
    const { id } = useParams();
    const [error, setError] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [event, setEvent] = useState(location.state?.event || null);
    const [loading, setLoading] = useState(!event);
    const [coverImage, setCoverImage] = useState(event?.coverImage || null);
    const [coverImageName, setCoverImageName] = useState(event?.originalCoverImageName || null);
    const [uploading, setUploading] = useState(false);
    const [isEditing, setIsEditing] = useState(true);

    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableHashtags, setAvailableHashtags] = useState([]);

    const [tickets, setTickets] = useState(event?.tickets || []);
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
    const [editingTicket, setEditingTicket] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchData = async () => {
            try {
                const categoriesResponse = await EventsService.getCategories();
                const hashtagsResponse = await EventsService.getHashtags();

                setAvailableCategories(categoriesResponse.data.map(c => ({ value: c.name, label: c.name })));
                setAvailableHashtags(hashtagsResponse.data.data.map(h => ({ value: h.name, label: h.name })));
            } catch (err) {
                toast({
                    title: t('request.category.error'),
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        fetchData();
    }, []);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();

    const handleSubmit = async (values, actions) => {
        try {
            if (!coverImage) {
                toast({
                    title: t('validation.error', 'Błąd walidacji'),
                    description: t('event.error.cover_image_empty', 'Wydarzenie musi posiadać zdjęcie.'),
                    status: 'error', duration: 5000, isClosable: true,
                });
                actions.setSubmitting(false);
                return;
            }

            if (values.ticketType === 'paid' && !isSuperAdmin) {
                const { hasPayment, hasKsef } = await checkIntegrationStatus();
                if (!hasPayment || !hasKsef) {
                    toast({
                        title: t('event.error.configure_payment_title'),
                        description: (
                            <Box>
                                <Text>{t('event.error.integration_incomplete')}</Text>
                                <Link
                                    href="/setup"
                                    isExternal
                                    display="block"
                                    mt={2}
                                    fontWeight="bold"
                                    textDecoration="underline"
                                >
                                    {t('event.error.go_to_account_settings')}
                                </Link>
                            </Box>
                        ),
                        status: 'error',
                        duration: null,
                        isClosable: true,
                    });
                    actions.setSubmitting(false);
                    return;
                }
            }

            const safeTickets = normalizeTickets(tickets);
            await EventsService.update({
                id: event?.id,
                title: values.title,
                parentTitle: values.parentEventTitle,
                address: values.address,
                url: values.url,
                platform: values.platform,
                startDate: values.startDate,
                startTime: values.startTime,
                endDate: values.endDate,
                endTime: values.endTime,
                maxParticipants: values.ticketType === 'free'
                    ? values.maxParticipants
                    : tickets.reduce((acc, ticket) => acc + (parseInt(ticket.availableQuantity) || 0), 0),
                description: values.description,
                coverImage: coverImage,
                coverImageName: coverImageName,
                isOnline: values.eventType === 'online',
                isFree: values.ticketType === 'free',
                tickets: safeTickets,
                showFreeSlotsInfo: values.showFreeSlotsInfo,
                directions: values.directions,
                categories: values.categories,
                hashtags: values.hashtags,
                host: {
                    id: event?.host?.id,
                    name: values.hostName,
                    phone: values.hostPhoneNumber ? `${values.hostPhonePrefix} ${values.hostPhoneNumber}` : '',
                    email: values.hostEmail,
                    info: values.hostInfo
                }
            });
            setError(null);
            toast({
                title: t('event.added'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            navigate('/');
        } catch (error) {
            setError(error.response?.data);
            toast({
                title: t('event.error.added'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            actions.setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        try {
            await EventsService.deleteEvent(event.id);
            toast({
                title: t('event.deleted'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            setDeleteError(null);
            navigate('/');
        } catch (error) {
            setDeleteError(error.response?.data);
            toast({
                title: t('event.error.deleted'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            onClose();
        }
    };

    const fetchEvent = async () => {
        setLoading(true);
        try {
            const res = await EventsService.getEvent(id);
            const event = res.data.data;
            setEvent(event);
            setCoverImage(event.coverImage);
            setCoverImageName(event.originalCoverImageName);
            setTickets(event.ticketTypes || []);
            setError(null);
        } catch (error) {
            setError(error);
            toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!event) {
            fetchEvent();
        }
    }, [event, id]);

    useEffect(() => {
        if (!loading && event) {
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, 0);
        }
    }, [loading, event]);

    const formatedSaleDate = (date) => {
        return date ? format(parseISO(date), "d MMMM yyyy, HH:mm", { locale: pl }) : '';
    }

    const formatCreateLabel = (inputValue) => `${t('form.create')} "${inputValue}"`;

    if (loading || !event) {
        return <Text>{t('event.loading')}</Text>;
    }
    let initialPhonePrefix = '+48';
    let initialPhoneNumber = '';
    if (event?.host?.phone) {
        const phoneParts = event.host.phone.match(/^(\+\d+)\s*(.*)$/);
        if (phoneParts) {
            initialPhonePrefix = phoneParts[1];
            initialPhoneNumber = phoneParts[2].replace(/\D/g, '');
        } else {
            initialPhoneNumber = event.host.phone.replace(/\D/g, '');
        }
    }

    return (
        <Flex w="100%" maxW="7xl" flexDirection={"column"} px={{ base: 4, md: 4 }} gap="32px" py={{ base: "40px", md: "80px" }}>
            <Flex w="100%" maxW="7xl" flexDirection={{ base: "column", md: "row" }} className="nav-wrapper" gap="16px" justifyContent={"space-between"} align={{ base: "flex-start", md: "center" }}>
                <Flex gap="16px" onClick={() => navigate(`/`)} cursor={"pointer"} align="center">
                    <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 15.5L1 8.5M1 8.5L8 1.5M1 8.5H19" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <Text className="nav" noOfLines={2}>
                        {event?.title}
                    </Text>
                </Flex>
                <Flex w={{ base: "100%", md: "auto" }} justify={{ base: "flex-start", md: "flex-end" }} gap={4}>
                    <Button
                        onClick={() => {
                            onOpen();
                        }}
                        borderRadius="0" borderColor={'#0A4725'} borderWidth={1}
                        bg="#F3F3F3"
                        color="#0A4725" px={{ base: 4, md: 8 }} py={5} fontWeight="500"
                        flex={{ base: 1, md: "initial" }}
                        rightIcon={
                            <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.5 5L15.633 17.142C15.5971 17.6466 15.3713 18.1188 15.0011 18.4636C14.6309 18.8083 14.1439 19 13.638 19H5.362C4.85614 19 4.36907 18.8083 3.99889 18.4636C3.6287 18.1188 3.40292 17.6466 3.367 17.142L2.5 5M7.5 9V15M11.5 9V15M12.5 5V2C12.5 1.73478 12.3946 1.48043 12.2071 1.29289C12.0196 1.10536 11.7652 1 11.5 1H7.5C7.23478 1 6.98043 1.10536 6.79289 1.29289C6.60536 1.48043 6.5 1.73478 6.5 2V5M1.5 5H17.5" stroke="#0A4725" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        }
                    >
                        {t('delete')}
                    </Button>
                </Flex>
            </Flex>
            {error && <ErrorEventForm error={error} t={t} name="event.edit.error" />}
            {deleteError && <ErrorEventForm error={deleteError} t={t} name="event.delete.error" />}

            {/* Informacje o autorze - widoczne tylko dla SuperAdmin */}
            {isSuperAdmin && (
                <Flex w="100%" maxW="7xl" className="event">
                    <Flex justify="center" gap={4} flexDirection={"column"} p={{ base: "16px", md: "16px" }} w="100%">
                        <Flex direction={"column"} gap={4} w="100%" bg="white" borderRadius="4px">
                            <Text fontSize="lg" fontWeight="bold" mb={2}>
                                {t('event.form.author.title', 'O autorze')}
                            </Text>
                            <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
                                {/* Lewa kolumna */}
                                <Flex direction="column" flex={1} gap={4}>
                                    <FormControl isDisabled>
                                        <FormLabel>{t('event.form.author.fullName', 'Imię i nazwisko')}</FormLabel>
                                        <Input value={event?.authorFullName || ''} />
                                    </FormControl>
                                    <FormControl isDisabled>
                                        <FormLabel>{t('event.form.author.address', 'Adres')}</FormLabel>
                                        <Input value={event?.authorAddress || ''} />
                                    </FormControl>
                                </Flex>
                                {/* Prawa kolumna */}
                                <Flex direction="column" flex={1} gap={4}>
                                    <FormControl isDisabled>
                                        <FormLabel>{t('event.form.author.additionalInfo', 'Dodatkowe informacje')}</FormLabel>
                                        <Input value={event?.authorAdditionalInfo || ''} />
                                    </FormControl>
                                    <FormControl isDisabled>
                                        <FormLabel>{t('event.form.author.paymentIntegration', 'Integracja płatności')}</FormLabel>
                                        <Input
                                            value={event?.authorHasPaymentIntegration
                                                ? t('event.form.author.paymentActive', 'Aktywna')
                                                : t('event.form.author.paymentInactive', 'Nieaktywna')}
                                        />
                                    </FormControl>
                                </Flex>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            )}

            <Flex w="100%" maxW="7xl" className="event">
                <Flex p={{ base: "16px", md: "16px" }} pb={0}>
                    <Text className="title">
                        {t('event.tab.about')}
                    </Text>
                </Flex>
                <Formik
                    initialValues={{
                        title: event?.title ?? '',
                        parentEventTitle: event?.parentEventTitle ?? '',
                        address: event?.address ?? '',
                        url: event?.url ?? '',
                        platform: event?.platform ?? '',
                        startDate: event?.startDate ?? '',
                        startTime: event?.startTime ?? '',
                        endDate: event?.endDate ?? '',
                        endTime: event?.endTime ?? '',
                        maxParticipants: event?.maxParticipants ?? '',
                        description: event?.description ?? '',
                        coverImage: event?.coverImage,
                        categories: event?.categories,
                        hashtags: event?.hashtags,
                        eventType: event?.isOnline ? 'online' : 'offline',
                        ticketType: event?.isFree ? 'free' : 'paid',
                        showFreeSlotsInfo: event?.showFreeSlotsInfo || false,
                        hostName: event?.host?.name ?? '',
                        hostPhonePrefix: initialPhonePrefix,
                        hostPhoneNumber: initialPhoneNumber,
                        hostEmail: event?.host?.email ?? '',
                        hostInfo: event?.host?.info ?? ''
                    }}
                    validationSchema={createEventValidationSchema(t, true)}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, errors, touched, values, setFieldValue, handleChange, handleBlur }) => (
                        <Form className="form">
                            <Flex direction={{ base: 'column', md: 'row' }} gap={6} p={{ base: "16px", md: "16px" }} pt={{ base: "8px", md: "8px" }}>
                                <Flex direction={"column"} flex={1} gap={4}>
                                    <InlineErrorInput name="title" value={values.title} label={t('event.form.name')} isDisabled={!isEditing} isRequired />
                                    <FormControl mb={4} isInvalid={errors.categories && touched.categories}>
                                        <FormLabel>{t('event.form.category')}</FormLabel>
                                        <Field name="categories">
                                            {({ field, form }) => (
                                                <>
                                                    <Select
                                                        isMulti
                                                        options={availableCategories}
                                                        value={field.value?.map(val => ({ value: val, label: val }))}
                                                        onChange={selectedOptions => {
                                                            const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                                            form.setFieldValue('categories', values);
                                                        }}
                                                        placeholder={t('event.form.placeholder.category')}
                                                        isClearable
                                                        isSearchable
                                                        maxMenuHeight={150}
                                                        className="categories-select"
                                                        onBlur={() => form.setFieldTouched('categories', true)}
                                                        isDisabled={!isEditing}
                                                    />
                                                    <FormErrorMessage>{errors.categories}</FormErrorMessage>
                                                </>
                                            )}
                                        </Field>
                                    </FormControl>
                                    <FormControl mb={4} isInvalid={errors.hashtags && touched.hashtags}>
                                        <FormLabel>{t('event.form.hashtags')}</FormLabel>
                                        <Field name="hashtags">
                                            {({ field, form }) => (
                                                <>
                                                    <CreatableSelect
                                                        isMulti
                                                        options={availableHashtags}
                                                        value={field.value?.map(val => ({ value: val, label: val }))}
                                                        onChange={selectedOptions => {
                                                            const values = selectedOptions ? selectedOptions.map(opt => opt.value.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean) : [];
                                                            const uniqueValues = [...new Set(values)];
                                                            form.setFieldValue('hashtags', uniqueValues);
                                                        }}
                                                        placeholder={t('event.form.placeholder.hashtags')}
                                                        isClearable
                                                        isSearchable
                                                        maxMenuHeight={150}
                                                        className="hashtags-select"
                                                        formatCreateLabel={formatCreateLabel}
                                                        isDisabled={!isEditing}
                                                    />
                                                    <FormErrorMessage>{errors.hashtags}</FormErrorMessage>
                                                </>
                                            )}
                                        </Field>
                                    </FormControl>
                                    <FileUploadInput isEditing={isEditing} coverImage={coverImage} coverImageName={coverImageName} onFileSelect={async (file) => {
                                        setUploading(true);
                                        try {
                                            const res = await EventsService.uploadCoverImage(file);
                                            if (res.data) {
                                                setCoverImageName(file?.name);
                                                setCoverImage(res.data);
                                                toast({ title: t('upload.done'), status: "success" });
                                            }
                                        } catch {
                                            toast({ title: t('upload.error'), status: "error" });
                                        } finally {
                                            setUploading(false);
                                        }
                                    }} />
                                    <Flex gap={4} alignItems="flex-end" sx={{ '& label': { minH: { base: '44px', md: 'auto' }, display: 'flex', alignItems: 'flex-end', mb: 1, pb: 0 }, '& > div': { flex: 1, justifySelf: 'flex-end' } }}>
                                        <ErrorInput name="startDate" type="date" value={values.startDate} label={t('event.form.startDate')} isDisabled={!isEditing} isRequired />
                                        <ErrorInput name="startTime" type="time" value={values.startTime} label={t('event.form.startTime')} isDisabled={!isEditing} isRequired />
                                    </Flex>
                                    <Flex gap={4} alignItems="flex-end" sx={{ '& label': { minH: { base: '44px', md: 'auto' }, display: 'flex', alignItems: 'flex-end', mb: 1, pb: 0 }, '& > div': { flex: 1, justifySelf: 'flex-end' } }}>
                                        <ErrorInput name="endDate" type="date" value={values.endDate} label={t('event.form.endDate')} isDisabled={!isEditing} isRequired />
                                        <ErrorInput name="endTime" type="time" value={values.endTime} label={t('event.form.endTime')} isDisabled={!isEditing} isRequired />
                                    </Flex>
                                    <FormControl mb={4} isInvalid={errors.eventType && touched.eventType}>
                                        <Field name="eventType">
                                            {({ field, form }) => (
                                                <>
                                                    <Flex pb="1px" pt={'8px'}>
                                                        <Box
                                                            px={4}
                                                            py={'16px'}
                                                            flex="1"
                                                            textAlign={"center"}
                                                            borderBottom={field.value === 'offline' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                                                            cursor="pointer"
                                                            onClick={() => form.setFieldValue('eventType', 'offline')}
                                                        >

                                                            <Text fontWeight="medium">
                                                                <Checkbox
                                                                    isChecked={field.value === 'offline'}
                                                                    pointerEvents="none"
                                                                    mr={'10px'}
                                                                    mt={'5px'}
                                                                />
                                                                {t('event.form.eventType.offline')}
                                                            </Text>
                                                        </Box>
                                                        <Box
                                                            px={4}
                                                            py={'16px'}
                                                            flex="1"
                                                            textAlign={"center"}
                                                            borderBottom={field.value === 'online' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                                                            cursor="pointer"
                                                            onClick={() => form.setFieldValue('eventType', 'online')}
                                                        >
                                                            <Text fontWeight="medium">
                                                                <Checkbox
                                                                    isChecked={field.value === 'online'}
                                                                    pointerEvents="none"
                                                                    mr={'10px'}
                                                                    mt={'5px'}
                                                                />
                                                                {t('event.form.eventType.online')}
                                                            </Text>
                                                        </Box>
                                                    </Flex>
                                                    <FormErrorMessage>{errors.eventType}</FormErrorMessage>
                                                </>
                                            )}
                                        </Field>
                                    </FormControl>
                                    <Field name="eventType">
                                        {({ field }) =>
                                            field.value === 'offline' ? (
                                                <>
                                                    <AddressAutocompleteInput name="address" label={t('event.form.eventType.address')} value={values.address} isDisabled={!isEditing}
                                                        onChange={handleChange} onBlur={handleBlur} error={errors.address} touched={touched.address} isRequired={values.eventType === 'offline'}
                                                    />
                                                    <InlineErrorInput name="directions" label={t('event.form.eventType.directions')} value={values.directions} isDisabled={!isEditing}
                                                        onChange={handleChange} onBlur={handleBlur} error={errors.directions} touched={touched.directions}
                                                        as={Textarea} rows={4} h={'100%'}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <InlineErrorInput name="platform" label={t('event.form.eventType.platform', 'Nazwa platformy')} value={values.platform} isDisabled={!isEditing}
                                                        onChange={handleChange} onBlur={handleBlur} error={errors.platform} touched={touched.platform} isRequired={values.eventType === 'online'}
                                                    />
                                                    <InlineErrorInput name="url" label={t('event.form.eventType.url', 'Link do wydarzenia')} value={values.url} isDisabled={!isEditing}
                                                        onChange={handleChange} onBlur={handleBlur} error={errors.url} touched={touched.url} isRequired={values.eventType === 'online'}
                                                    />
                                                </>
                                            )
                                        }
                                    </Field>
                                </Flex>
                                <Box flex={1}>
                                    <QuillEditor
                                        label={t('event.form.description')}
                                        value={values.description}
                                        onChange={(val) => setFieldValue('description', val)}
                                    />
                                    {errors.description && touched.description && (
                                        <FormErrorMessage style={{ display: 'block' }}>{errors.description}</FormErrorMessage>
                                    )}
                                </Box>
                            </Flex>

                            <Flex w="100%" bg="#F3F3F3" h={'32px'}>
                            </Flex>
                            <Flex justify="center" gap={4} flexDirection={"column"} p={{ base: "16px", md: "16px" }}>

                                <Box w="100%" bg="white" borderRadius="4px">
                                    <Text className="title">{t('event.form.title')}</Text>
                                    <Text className="subtitle">
                                        {t('event.host.info')}
                                    </Text>
                                    <FormControl mb={4} isDisabled={!isEditing}>
                                        <Field name="ticketType" isInvalid={errors.ticketType && touched.ticketType}>
                                            {({ field, form }) => (
                                                <>
                                                    <Flex pb="1px" pt={'8px'}>
                                                        <Box
                                                            px={4}
                                                            py={'16px'}
                                                            flex="1"
                                                            textAlign={"center"}
                                                            borderBottom={field.value === 'paid' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                                                            cursor="pointer"
                                                            onClick={() => form.setFieldValue('ticketType', 'paid')}
                                                        >
                                                            <Flex align="center" justify="center" fontWeight="medium">
                                                                <Checkbox
                                                                    isChecked={field.value === 'paid'}
                                                                    pointerEvents="none"
                                                                    mr={'10px'}
                                                                    mt={'5px'}
                                                                />
                                                                {t('event.form.ticketType.paid')}
                                                            </Flex>
                                                        </Box>
                                                        <Box
                                                            px={4}
                                                            py={'16px'}
                                                            flex="1"
                                                            textAlign={"center"}
                                                            borderBottom={field.value === 'free' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                                                            cursor="pointer"
                                                            onClick={() => form.setFieldValue('ticketType', 'free')}
                                                        >
                                                            <Flex align="center" justify="center" fontWeight="medium">
                                                                <Checkbox
                                                                    isChecked={field.value === 'free'}
                                                                    pointerEvents="none"
                                                                    mr={'10px'}
                                                                    mt={'5px'}
                                                                />
                                                                {t('event.form.ticketType.free')}
                                                            </Flex>
                                                        </Box>
                                                    </Flex>
                                                    <FormErrorMessage>{errors.ticketType}</FormErrorMessage>
                                                    {/* Dla ticketType === 'paid' > tabelka */}
                                                    {field.value === 'paid' && (
                                                        <Box mb={4} mt={4}>
                                                            {tickets.length > 0 ? (
                                                                    <Box w="100%" borderRadius="4px" overflow="hidden">
                                                                        <Box display={{ base: 'none', lg: 'flex' }} px={4} py={2}>
                                                                            <Box flex="2">{t('event.form.tickets.th.kind')}</Box>
                                                                            <Box flex="1">{t('event.form.tickets.th.price')}</Box>
                                                                            <Box flex="2">{t('event.form.tickets.th.from')}</Box>
                                                                            <Box flex="2">{t('event.form.tickets.th.to')}</Box>
                                                                            <Box flex="1" textAlign="center">{t('event.form.tickets.th.quantity')}</Box>
                                                                            <Box flex="0.5"></Box>
                                                                        </Box>
                                                                        <Flex direction="column" gap={{ base: 4, lg: 0 }}>
                                                                        {tickets.map((ticket, index) => (
                                                                            <Box key={index} display="flex" flexDirection={{ base: 'column', lg: 'row' }} bg="#F3F3F3" fontWeight="bold" alignItems={{ base: 'flex-start', lg: 'center' }} px={4} py={{ base: 4, lg: 2 }} borderRadius={{ base: 4, lg: 0 }}>
                                                                                <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.kind')}:</Text>
                                                                                    <Text as="span">{ticket.name}</Text>
                                                                                </Box>
                                                                                <Box flex="1" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.price')}:</Text>
                                                                                    <Text as="span">{ticket.price} zł</Text>
                                                                                </Box>
                                                                                <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.from')}:</Text>
                                                                                    <Text as="span" textAlign={{ base: "right", lg: "left" }}>{formatedSaleDate(ticket.saleStart)}</Text>
                                                                                </Box>
                                                                                <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.to')}:</Text>
                                                                                    <Text as="span" textAlign={{ base: "right", lg: "left" }}>{formatedSaleDate(ticket.saleEnd)}</Text>
                                                                                </Box>
                                                                                <Box flex="1" display="flex" justifyContent={{ base: 'space-between', lg: 'center' }} w="100%" mb={{ base: 4, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.quantity')}:</Text>
                                                                                    <Text as="span" textAlign={{ base: "right", lg: "center" }}>{ticket.availableQuantity}</Text>
                                                                                </Box>
                                                                                <Box flex="0.5" display="flex" gap={2} justifyContent={{ base: 'flex-start', lg: 'flex-end' }} w="100%">
                                                                                    <Button size="sm" isDisabled={!isEditing} onClick={() => {
                                                                                        setTickets(prev => prev.filter((_, i) => i !== index));
                                                                                    }}>
                                                                                        <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                            <path d="M14.125 4.55556L13.3664 15.3484C13.3349 15.797 13.1374 16.2167 12.8135 16.5232C12.4896 16.8296 12.0634 17 11.6208 17H4.37925C3.93662 17 3.51043 16.8296 3.18652 16.5232C2.86262 16.2167 2.66505 15.797 2.63363 15.3484L1.875 4.55556M6.25 8.11111V13.4444M9.75 8.11111V13.4444M10.625 4.55556V1.88889C10.625 1.65314 10.5328 1.42705 10.3687 1.26035C10.2046 1.09365 9.98206 1 9.75 1H6.25C6.01794 1 5.79538 1.09365 5.63128 1.26035C5.46719 1.42705 5.375 1.65314 5.375 1.88889V4.55556M1 4.55556H15" stroke="#089A7A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                        </svg>
                                                                                    </Button>
                                                                                    <Button size="sm" isDisabled={!isEditing} onClick={() => {
                                                                                        setEditingTicket(ticket);
                                                                                        setIsTicketModalOpen(true);
                                                                                    }}>
                                                                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                            <path d="M17.414 2.58594C17.0389 2.211 16.5303 2.00037 16 2.00037C15.4697 2.00037 14.9611 2.211 14.586 2.58594L7 10.1719V12.9999H9.828L17.414 5.41394C17.7889 5.03888 17.9996 4.53027 17.9996 3.99994C17.9996 3.46961 17.7889 2.96099 17.414 2.58594Z" fill="#089A7A" />
                                                                                            <path fillRule="evenodd" clipRule="evenodd" d="M2 6C2 5.46957 2.21071 4.96086 2.58579 4.58579C2.96086 4.21071 3.46957 4 4 4H8C8.26522 4 8.51957 4.10536 8.70711 4.29289C8.89464 4.48043 9 4.73478 9 5C9 5.26522 8.89464 5.51957 8.70711 5.70711C8.51957 5.89464 8.26522 6 8 6H4V16H14V12C14 11.7348 14.1054 11.4804 14.2929 11.2929C14.4804 11.1054 14.7348 11 15 11C15.2652 11 15.5196 11.1054 15.7071 11.2929C15.8946 11.4804 16 11.7348 16 12V16C16 16.5304 15.7893 17.0391 15.4142 17.4142C15.0391 17.7893 14.5304 18 14 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V6Z" fill="#089A7A" />
                                                                                        </svg>
                                                                                        ️</Button>
                                                                                </Box>
                                                                            </Box>
                                                                        ))}
                                                                        </Flex>
                                                                    </Box>
                                                            ) : (
                                                                <Text mt={2} mb={2}>{t('event.form.tickets.not_found')}</Text>
                                                            )}

                                                            <Flex justify="center" gap={4} mt={6}>
                                                                <Button
                                                                    onClick={() => {
                                                                        setEditingTicket(null);
                                                                        setIsTicketModalOpen(true);
                                                                    }}
                                                                    isDisabled={!isEditing}
                                                                    isLoading={isSubmitting} bg="brand.500" color="white" borderRadius={'4px'} _hover={{ bg: 'brand.600' }} px="48px" py="25px">
                                                                    <Text mr={'10px'}>
                                                                        {t('event.form.tickets.add')}
                                                                    </Text>
                                                                    <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M8.5 5.66667V8M8.5 8V10.3333M8.5 8H10.8333M8.5 8H6.16667M15.5 8C15.5 8.91925 15.3189 9.82951 14.9672 10.6788C14.6154 11.5281 14.0998 12.2997 13.4497 12.9497C12.7997 13.5998 12.0281 14.1154 11.1788 14.4672C10.3295 14.8189 9.41925 15 8.5 15C7.58075 15 6.6705 14.8189 5.82122 14.4672C4.97194 14.1154 4.20026 13.5998 3.55025 12.9497C2.90024 12.2997 2.38463 11.5281 2.03284 10.6788C1.68106 9.82951 1.5 8.91925 1.5 8C1.5 6.14348 2.2375 4.36301 3.55025 3.05025C4.86301 1.7375 6.64348 1 8.5 1C10.3565 1 12.137 1.7375 13.4497 3.05025C14.7625 4.36301 15.5 6.14348 15.5 8Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                </Button>
                                                            </Flex>
                                                        </Box>
                                                    )}
                                                    <TicketModal
                                                        isOpen={isTicketModalOpen}
                                                        onClose={() => setIsTicketModalOpen(false)}
                                                        onSave={(ticketData) => {
                                                            if (editingTicket) {
                                                                setTickets(prev =>
                                                                    prev.map(t => (t === editingTicket ? ticketData : t))
                                                                );
                                                            } else {
                                                                setTickets(prev => [...prev, ticketData]);
                                                            }
                                                        }}
                                                        initialData={editingTicket}
                                                    />

                                                    {/* Dla ticketType === 'free' > dwa pola */}
                                                    {field.value === 'free' && (
                                                        <>
                                                            <InlineErrorInput name="maxParticipants" type="number" label={t('event.form.maxParticipants')} value={values.maxParticipants} mb={2}
                                                                onChange={handleChange} onBlur={handleBlur} error={errors.maxParticipants} touched={touched.maxParticipants}
                                                                isRequired isDisabled={!isEditing}
                                                            />
                                                            <FormControl mb={4}>
                                                                <Checkbox
                                                                    isChecked={form.values.showFreeSlotsInfo}
                                                                    onChange={e => form.setFieldValue('showFreeSlotsInfo', e.target.checked)}
                                                                    isDisabled={!isEditing}
                                                                >
                                                                    {t('event.form.showFreeSlotsInfo')}
                                                                </Checkbox>
                                                            </FormControl>
                                                        </>
                                                    )}

                                                </>
                                            )}
                                        </Field>
                                    </FormControl>
                                </Box>
                            </Flex>

                            <Flex w="100%" bg="#F3F3F3" h={'32px'}>
                            </Flex>

                            <Flex justify="center" gap={4} flexDirection={"column"} p={{ base: "16px", md: "16px" }}>

                                <Flex direction={"column"} gap={4} w="100%" bg="white" borderRadius="4px">
                                    <Text fontSize="lg" fontWeight="bold" mb={2}>{t('event.form.host.title')}</Text>
                                    <InlineErrorInput name="hostName" label={t('event.form.host.name')} value={values.hostName} isDisabled={!isEditing}
                                        onChange={handleChange} onBlur={handleBlur} error={errors.hostName} touched={touched.hostName} isRequired
                                    />
                                    <InlineErrorInput name="hostPhoneNumber" label={t('event.form.host.phone')} value={values.hostPhoneNumber} placeholder="123456789" isDisabled={!isEditing} useFormik={false}
                                        onChange={(e) => {
                                            const numericValue = e.target.value.replace(/\D/g, '');
                                            e.target.value = numericValue;
                                            setFieldValue('hostPhoneNumber', numericValue);
                                        }}
                                        onBlur={handleBlur} error={errors.hostPhoneNumber} touched={touched.hostPhoneNumber}
                                        leftAddon={
                                            <Menu autoSelect={false} strategy="fixed" isLazy>
                                                <MenuButton
                                                    type="button"
                                                    disabled={!isEditing}
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        outline: 'none',
                                                        cursor: isEditing ? 'pointer' : 'default',
                                                        padding: '0 8px',
                                                        minWidth: '70px',
                                                        textAlign: 'left'
                                                    }}
                                                >
                                                    <Flex align="center" gap={2}>
                                                        <ReactCountryFlag
                                                            countryCode={phonePrefixes.find(p => p.value === values.hostPhonePrefix)?.code || 'PL'}
                                                            svg
                                                            style={{ width: '1.2em', height: '1.2em' }}
                                                        />
                                                        <Text>{values.hostPhonePrefix}</Text>
                                                        <ChevronDownIcon w={4} h={4} color="gray.500" />
                                                    </Flex>
                                                </MenuButton>
                                                <Portal>
                                                    <MenuList maxH="300px" overflowY="auto" zIndex={1000}>
                                                        {phonePrefixes.map(prefix => (
                                                            <MenuItem
                                                                key={prefix.code}
                                                                onClick={() => setFieldValue('hostPhonePrefix', prefix.value)}
                                                            >
                                                                <Flex align="center" gap={2}>
                                                                    <ReactCountryFlag countryCode={prefix.code} svg style={{ width: '1.2em', height: '1.2em' }} />
                                                                    <Text>{prefix.label}</Text>
                                                                </Flex>
                                                            </MenuItem>
                                                        ))}
                                                    </MenuList>
                                                </Portal>
                                            </Menu>
                                        }
                                    />
                                    <InlineErrorInput name="hostEmail" label={t('event.form.host.email')} value={values.hostEmail} isDisabled={!isEditing}
                                        onChange={handleChange} onBlur={handleBlur} error={errors.hostEmail} touched={touched.hostEmail}
                                    />
                                    <Box mt={4}>
                                        <QuillEditor
                                            label={t('event.form.host.info')}
                                            value={values.hostInfo}
                                            onChange={(val) => setFieldValue('hostInfo', val)}
                                            isDisabled={!isEditing}
                                        />
                                        <Text fontSize="sm" color="gray.500" textAlign="right" mt={1}>
                                            {values.hostInfo?.length || 0}/2000
                                        </Text>
                                        {errors.hostInfo && touched.hostInfo && (
                                            <FormErrorMessage style={{ display: 'block' }}>{errors.hostInfo}</FormErrorMessage>
                                        )}
                                    </Box>
                                </Flex>
                            </Flex>

                            <Flex justify="right" gap={4} mt={6} p={'24px'}>
                                <Button variant="outline" borderRadius={0} onClick={() => navigate('/')} py="25px" hidden={!isEditing}>{t('cancel')}</Button>
                                <Button type="submit" isLoading={isSubmitting} bg="brand.500" color="white" borderRadius={0} _hover={{ bg: 'brand.600' }} px="48px" py="25px" hidden={!isEditing}>
                                    {t('save')}
                                </Button>
                            </Flex>
                        </Form>
                    )}
                </Formik>
            </Flex>
            {/* Modal potwierdzający usunięcie */}
            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
                isCentered
            >
                <AlertDialogOverlay bg="blackAlpha.300">
                    <AlertDialogContent borderRadius="0" maxW="660px" mx={4} textAlign="center" px={6} py={6} position="relative">
                        <CloseButton
                            position="absolute"
                            top="16px"
                            right="16px"
                            onClick={onClose}
                            color="black"
                        />
                        <AlertDialogHeader
                            fontSize="20px"
                            fontWeight="bold"
                            color="black"
                            pb={2}
                            textAlign="left"
                        >
                            {t('event.delete.title')}
                        </AlertDialogHeader>

                        <AlertDialogBody fontSize="16px" color="black" whiteSpace="pre-line" py="48px">
                            <Text fontWeight={600} fontSize="22px">
                                {t('event.confirm.delete')}
                            </Text>

                            <Box mt={4}>
                                {t('event.info.delete')}
                            </Box>
                        </AlertDialogBody>

                        <AlertDialogFooter justifyContent="center" pt={6}>
                            <Button
                                ref={cancelRef}
                                onClick={onClose}
                                borderRadius="0"
                                border="1px solid #0A4725"
                                bg="white"
                                color="#0A4725"
                                px={8}
                                py={5}
                                fontWeight="500"
                                _hover={{ bg: "#f0f0f0" }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                onClick={handleDelete}
                                borderRadius="0"
                                bg="#0A4725"
                                color="white"
                                ml={4}
                                px={8}
                                py={5}
                                fontWeight="500"
                                rightIcon={
                                    <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M16.5 5L15.633 17.142C15.5971 17.6466 15.3713 18.1188 15.0011 18.4636C14.6309 18.8083 14.1439 19 13.638 19H5.362C4.85614 19 4.36907 18.8083 3.99889 18.4636C3.6287 18.1188 3.40292 17.6466 3.367 17.142L2.5 5M7.5 9V15M11.5 9V15M12.5 5V2C12.5 1.73478 12.3946 1.48043 12.2071 1.29289C12.0196 1.10536 11.7652 1 11.5 1H7.5C7.23478 1 6.98043 1.10536 6.79289 1.29289C6.60536 1.48043 6.5 1.73478 6.5 2V5M1.5 5H17.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                                _hover={{ bg: "#0c5a32" }}
                            >
                                {t('delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    );
};

export default EditEventForm;
